import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import './FrequentlyBoughtTogetherWidget.style.scss';
import { isRoStore } from 'Util/Store';
import Loader from 'Component/Loader';

const FrequentlyBoughtTogetherWidget = ({
                                            products,
                                            isLoading,
                                            error,
                                            handleAddToCart,
                                            title,
                                            currency,
                                            isAdding,
                                        }) => {
    const [selectedProducts, setSelectedProducts] = useState({});

    const isSpecialPriceValid = (specialData) => {
        if (!specialData || !specialData.price || !specialData.startDate || !specialData.endDate) {
            return false;
        }
        const now = new Date();
        const startDate = new Date(specialData.startDate);
        const endDate = new Date(specialData.endDate);
        return now >= startDate && now <= endDate;
    };

    useEffect(() => {
        if (products && products.length > 0) {
            const allSelected = products.reduce((acc, product) => {
                acc[product.sku] = product.inStock; // Select only in-stock products
                return acc;
            }, {});
            setSelectedProducts(allSelected);
        }
    }, [products]);

    const totalPrice = products
        .filter((product) => selectedProducts[product.sku] && product.inStock) // Exclude out-of-stock items
        .reduce((acc, product) => {
            const specialValid = isSpecialPriceValid(product.cartRuleSpecialPriceData);
            const price = specialValid
                ? product.cartRuleSpecialPriceData.price
                : product.price_range.minimum_price.final_price.value;
            return acc + parseFloat(price);
        }, 0)
        .toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    const toggleProductSelection = (sku) => {
        const product = products.find((p) => p.sku === sku);
        if (!product || !product.inStock) return; // Prevent toggling out-of-stock products

        setSelectedProducts((prevSelected) => ({
            ...prevSelected,
            [sku]: !prevSelected[sku],
        }));
    };

    const handleAddSelectedToCart = () => {
        const selectedSkus = products
            .filter((product) => selectedProducts[product.sku] && product.inStock)
            .map((product) => product.sku);

        handleAddToCart(selectedSkus);
    };

    if (isLoading) {
        return <Loader isLoading={ isLoading } />;
    }

    if (error) {
        return null;
    }

    return (
        <div className="FrequentlyBoughtTogetherWidget">
            <h3 className="FrequentlyBoughtTogetherWidget-Title">{title}</h3>
            <div className="FrequentlyBoughtTogetherWidget-Content">
                <div className="FrequentlyBoughtTogetherWidget-Products">
                    {products.map((product, index) => {
                        const isSelected = !!selectedProducts[product.sku];
                        const specialValid = isSpecialPriceValid(product.cartRuleSpecialPriceData);
                        const specialPrice = specialValid
                            ? product.cartRuleSpecialPriceData.price
                            : null;
                        const discountPrice = product.price_range.minimum_price.final_price.value;
                        const regularPrice = product.price_range.minimum_price.regular_price.value;
                        const isOutOfStock = !product.inStock;

                        return (
                            <React.Fragment key={product.sku}>
                                <div
                                    className={`FrequentlyBoughtTogetherWidget-ProductCard ${
                                        isSelected ? 'Selected' : ''
                                    } ${isOutOfStock ? 'OutOfStock' : ''}`}
                                    onClick={(e) => {
                                        if (
                                            !isOutOfStock && // Prevent clicking for out-of-stock products
                                            e.target.tagName.toLowerCase() !== 'a' &&
                                            e.target.type !== 'checkbox'
                                        ) {
                                            toggleProductSelection(product.sku);
                                        }
                                    }}
                                >
                                    <div className="FrequentlyBoughtTogetherWidget-CheckboxWrapper">
                                        <input
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={() => toggleProductSelection(product.sku)}
                                            disabled={isOutOfStock} // Disable checkbox for out-of-stock products
                                        />
                                        {isSelected && (
                                            <span className="FrequentlyBoughtTogetherWidget-Checkmark">
                                                ✓
                                            </span>
                                        )}
                                    </div>
                                    <img
                                        src={product.small_image.url}
                                        alt={product.name}
                                        className="FrequentlyBoughtTogetherWidget-Image"
                                    />
                                    <div className="FrequentlyBoughtTogetherWidget-Details">
                                        <a
                                            href={product.url}
                                            className="FrequentlyBoughtTogetherWidget-Link"
                                        >
                                            <span className="FrequentlyBoughtTogetherWidget-Name">
                                                {product.name}
                                            </span>
                                        </a>
                                        <div className="FrequentlyBoughtTogetherWidget-Price">
                                            {specialValid ? (
                                                <>
                                                    <span className="SpecialPrice">
                                                        {specialPrice.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{' '}
                                                        {isRoStore() ? 'LEI' : 'EUR'}
                                                    </span>
                                                    <del>
                                                        {regularPrice.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{' '}
                                                        {isRoStore() ? 'LEI' : 'EUR'}
                                                    </del>
                                                </>
                                            ) : discountPrice !== regularPrice ? (
                                                <>
                                                    <span className="SpecialPrice">
                                                        {discountPrice.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{' '}
                                                        {isRoStore() ? 'LEI' : 'EUR'}
                                                    </span>
                                                    <del>
                                                        {regularPrice.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{' '}
                                                        {isRoStore() ? 'LEI' : 'EUR'}
                                                    </del>
                                                </>
                                            ) : (
                                                <span>
                                                    {regularPrice.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{' '}
                                                    {isRoStore() ? 'LEI' : 'EUR'}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    {isOutOfStock && (
                                        <div className="FrequentlyBoughtTogetherWidget-OutOfStockLabel">
                                            {__('Out of stock')}
                                        </div>
                                    )}
                                </div>
                                {index < products.length - 1 && (
                                    <div className="FrequentlyBoughtTogetherWidget-Separator">
                                        +
                                    </div>
                                )}
                            </React.Fragment>
                        );
                    })}
                    <div className="FrequentlyBoughtTogetherWidget-Separator">=</div>
                    <div className="FrequentlyBoughtTogetherWidget-Action">
                        <div className="FrequentlyBoughtTogetherWidget-TotalPrice">
                            <span>{__('Total Price: ')}</span>
                            <span>
                                <strong>
                                    {totalPrice} {isRoStore() ? 'LEI' : 'EUR'}
                                </strong>
                            </span>
                        </div>
                        <button
                            className="FrequentlyBoughtTogetherWidget-Button Button ProductActions-AddToCart"
                            onClick={handleAddSelectedToCart}
                            disabled={isAdding}
                        >
                            {isAdding ? <span>{__('Adding...')}</span> : <span>{__('Add Selected to Cart')}</span>}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

FrequentlyBoughtTogetherWidget.propTypes = {
    products: PropTypes.arrayOf(
        PropTypes.shape({
            sku: PropTypes.string.isRequired,
            name: PropTypes.string.isRequired,
            small_image: PropTypes.object.isRequired,
            price_range: PropTypes.shape({
                minimum_price: PropTypes.shape({
                    final_price: PropTypes.shape({
                        value: PropTypes.number.isRequired,
                        currency: PropTypes.string,
                    }),
                    regular_price: PropTypes.shape({
                        value: PropTypes.number.isRequired,
                        currency: PropTypes.string,
                    }),
                }),
            }),
            url: PropTypes.string.isRequired,
            cartRuleSpecialPriceData: PropTypes.shape({
                price: PropTypes.number,
                startDate: PropTypes.string,
                endDate: PropTypes.string,
            }),
            inStock: PropTypes.bool.isRequired, // Ensure inStock is required
        })
    ).isRequired,
    isLoading: PropTypes.bool.isRequired,
    error: PropTypes.object,
    handleAddToCart: PropTypes.func.isRequired,
    title: PropTypes.string.isRequired,
    currency: PropTypes.string.isRequired,
    isAdding: PropTypes.bool.isRequired,
};

export default FrequentlyBoughtTogetherWidget;
